import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isNil from 'lodash/isNil';
import { v4 as uuid } from 'uuid';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CreatableSelect } from '@atlaskit/select';
import type { ActionMeta } from '@atlassian/jira-common-components-picker/src/model.tsx';
import performance from '@atlassian/jira-common-performance/src/performance.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { SessionId } from '@atlassian/jira-issue-analytics/src/common/types.tsx';
import { memoizedHashString } from '@atlassian/jira-issue-analytics/src/utils/hash-attribute/index.tsx';
import type { Option } from '@atlassian/jira-issue-field-select-base/src/common/types.tsx';
import {
	fireUIAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
	AnalyticsEventToProps,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';
import type { Props } from './types.tsx';
import {
	filterOptions,
	flattenOptions,
	getOptionValuesLengths,
	getSafeValue,
	getSafeOptionsForAnalytics,
} from './utils.tsx';

const ANALYTICS_PAYLOAD_TRUNCATION_SIZE = 20;

const analyticsProps = {
	onChange: 'valueChanged',
	onInputChange: 'inputChanged',
	onMenuOpen: 'menuOpened',
	onMenuClose: 'menuClosed',
} as const;

export const CreatableSelectWithAnalytics = AnalyticsEventToProps(
	'select',
	analyticsProps,
)(CreatableSelect);

const CreatableSelectWithAnalyticsView = (props: Props) => {
	const {
		fieldId,
		isLoading,
		onChange,
		onInputChange,
		options = [],
		value,
		sessionId: customSessionId,
		canCreateNewItem = false,
		inputId,
		isRequired,
		ariaLabel = '',
	} = props;
	const { formatMessage } = useIntl();
	const hasSessionStarted = useRef(false);
	const sessionId = useRef<SessionId>(customSessionId);
	const sessionStartedAt = useRef<number>(0);
	const endSessionRef = useRef<unknown>(undefined);

	const [queryAtSetValue, setQueryAtSetValue] = useState<string>('');
	const [savedQuery, setSavedQuery] = useState<string>('');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const flatOptions = useMemo(() => flattenOptions(options), [options]);

	const getAnalyticsAttributes = useCallback(
		(query: string): Attributes => {
			const selectedOptions: Option[] = !isNil(value) ? value : [];

			let filteredOptions = filterOptions(flatOptions, query);
			const optionsLength = filteredOptions.length;

			// truncate to reduce payload size
			filteredOptions = filteredOptions.slice(0, ANALYTICS_PAYLOAD_TRUNCATION_SIZE);
			const safeFilteredOptions = getSafeOptionsForAnalytics(filteredOptions);

			return {
				inputId,
				required: isRequired,
				fieldId,
				isLoading,
				optionsLength,
				componentName: 'select',
				// TODO: update the lib's version after the bump
				packageName: '@atlaskit/select',
				optionValuesSafeForAnalytics: false,
				options: safeFilteredOptions,
				optionsLengths: getOptionValuesLengths(filteredOptions),
				queryLength: query.length,
				queryHash: query === '' ? query : memoizedHashString(query),
				selectedOptions: getSafeOptionsForAnalytics(selectedOptions),
				selectedOptionsLengths: getOptionValuesLengths(selectedOptions),
				sessionId: sessionId.current,
				truncationSize: ANALYTICS_PAYLOAD_TRUNCATION_SIZE,
			};
		},
		[value, flatOptions, inputId, isRequired, fieldId, isLoading],
	);

	const getItemOrder = useCallback(
		(selectedItem: Option) => {
			const matchedIndexes: Array<number> = [];

			flatOptions.forEach((option, index) => {
				if (option.value === selectedItem.value) {
					matchedIndexes.push(index);
				}
			});

			return matchedIndexes.length > 0 ? matchedIndexes[0] : -1;
		},
		[flatOptions],
	);

	const fireUIAnalyticsEvent = useCallback(
		(analyticsEvent: UIAnalyticsEvent, query: string, extraAttributes?: Attributes) => {
			const attributes: Attributes = {
				...getAnalyticsAttributes(query),
				...extraAttributes,
			};

			fireUIAnalytics(analyticsEvent, attributes);
		},
		[getAnalyticsAttributes],
	);

	const fireTrackAnalyticsEvent = useCallback(
		(
			analyticsEvent: UIAnalyticsEvent,
			action: string,
			actionSubject: string,
			extraAttributes?: Attributes,
		) => {
			const attributes: Attributes = {
				...getAnalyticsAttributes(savedQuery),
				...extraAttributes,
			};

			fireTrackAnalytics(analyticsEvent, `${actionSubject} ${action}`, attributes);
		},
		[getAnalyticsAttributes, savedQuery],
	);

	const startSession = useCallback(() => {
		if (sessionId.current === undefined) {
			sessionId.current = uuid();
		}

		sessionStartedAt.current = performance.now();

		fireTrackAnalyticsEvent(
			createAnalyticsEvent({ action: 'started' }),
			'started',
			'optionSelectionSession',
		);
	}, [createAnalyticsEvent, fireTrackAnalyticsEvent, sessionId, sessionStartedAt]);

	const endSession = useCallback(() => {
		if (sessionId.current !== undefined) {
			const currentStartedAt = sessionStartedAt.current;
			const elapsedTime = currentStartedAt === 0 ? performance.now() - currentStartedAt : 0;

			fireTrackAnalyticsEvent(
				createAnalyticsEvent({ action: 'ended' }),
				'ended',
				'optionSelectionSession',
				{ elapsedTime },
			);
		}

		sessionId.current = undefined;
		sessionStartedAt.current = 0;
	}, [createAnalyticsEvent, fireTrackAnalyticsEvent, sessionId, sessionStartedAt]);

	const restartSession = useCallback(() => {
		endSession();
		startSession();
	}, [endSession, startSession]);

	const fireInputChangedEvent = useCallback(
		(query: string, actionMeta: ActionMeta<Option>, analyticsEvent: UIAnalyticsEvent) => {
			if (actionMeta.action === 'set-value') {
				// Save query for option changed analytic
				setQueryAtSetValue(savedQuery);
			}

			setSavedQuery(query);

			analyticsEvent.update({
				action: 'changed',
				actionSubject: 'filter',
			});

			fireUIAnalyticsEvent(analyticsEvent, query);
		},
		[fireUIAnalyticsEvent, savedQuery, setSavedQuery, setQueryAtSetValue],
	);

	const fireOptionCreatedEvent = useCallback(
		(option: Option, analyticsEvent: UIAnalyticsEvent) => {
			analyticsEvent.update({
				action: 'created',
				actionSubject: 'option',
			});

			const oldSessionId = sessionId.current;

			restartSession();

			fireUIAnalyticsEvent(analyticsEvent, queryAtSetValue, {
				selectedOption: getSafeValue(option.value),
				sessionId: oldSessionId,
			});
		},
		[fireUIAnalyticsEvent, queryAtSetValue, restartSession, sessionId],
	);

	const fireOptionRemovedEvent = useCallback(
		(option: Option | null | undefined, analyticsEvent: UIAnalyticsEvent) => {
			analyticsEvent.update({
				action: 'removed',
				actionSubject: 'option',
			});

			fireUIAnalyticsEvent(analyticsEvent, savedQuery, {
				removedItem: option ? getSafeValue(option.value) : undefined,
			});
		},
		[fireUIAnalyticsEvent, savedQuery],
	);

	const fireOptionSelectedEvent = useCallback(
		(option: Option, analyticsEvent: UIAnalyticsEvent) => {
			analyticsEvent.update({
				action: 'changed',
				actionSubject: 'option',
			});

			const oldSessionId = sessionId.current;

			restartSession();

			fireUIAnalyticsEvent(analyticsEvent, queryAtSetValue, {
				selectedOption: getSafeValue(option.value),
				selectedOptionLength: option.value !== null ? `${option.value}`.length : 0,
				selectedOrder: getItemOrder(option),
				sessionId: oldSessionId,
			});
		},
		[fireUIAnalyticsEvent, getItemOrder, queryAtSetValue, restartSession, sessionId],
	);

	const onChangeWithAnalytics = useCallback(
		(
			selectedOptionOrOptions: Option | Option[] | null,
			actionMeta: ActionMeta<Option>,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			const selectedOption = Array.isArray(selectedOptionOrOptions)
				? selectedOptionOrOptions[selectedOptionOrOptions.length - 1]
				: selectedOptionOrOptions;

			switch (actionMeta.action) {
				case 'create-option':
					if (selectedOption !== null) {
						fireOptionCreatedEvent(selectedOption, analyticsEvent);
					}
					break;
				case 'remove-value':
				case 'pop-value':
				case 'deselect-option':
					fireOptionRemovedEvent(actionMeta.removedValue || actionMeta.option, analyticsEvent);
					break;
				case 'select-option':
					if (selectedOption !== null) {
						fireOptionSelectedEvent(selectedOption, analyticsEvent);
					}
					break;
				default:
					break;
			}

			onChange && onChange(selectedOptionOrOptions, actionMeta);
		},
		[fireOptionCreatedEvent, fireOptionRemovedEvent, fireOptionSelectedEvent, onChange],
	);

	const onInputChangeWithAnalytics = useCallback(
		(query: string, actionMeta: ActionMeta<Option>, analyticsEvent: UIAnalyticsEvent) => {
			if (actionMeta.action === 'input-change' || actionMeta.action === 'set-value') {
				fireInputChangedEvent(query, actionMeta, analyticsEvent);
			}

			onInputChange && onInputChange(query, sessionId.current);
		},
		[fireInputChangedEvent, onInputChange, sessionId],
	);

	const isValidNewOption = useCallback(() => !!canCreateNewItem, [canCreateNewItem]);

	useEffect(() => {
		if (!hasSessionStarted.current) {
			startSession();
			fireInputChangedEvent('', { action: 'fake-action' }, createAnalyticsEvent({}));
			hasSessionStarted.current = true;
		}
	}, [createAnalyticsEvent, fireInputChangedEvent, startSession]);

	useEffect(() => {
		endSessionRef.current = endSession;
	}, [endSession]);

	useEffect(
		() => () => {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			endSessionRef.current && endSessionRef.current();
		},
		// In order to avoid triggering this cleanup when the endSession callback is updated by adding a dependency to endSession here,
		// we call it via a ref instead
		[],
	);

	const { isRequired: required, ...propsWithoutRequired } = props;

	return (
		<CreatableSelectWithAnalytics
			{...propsWithoutRequired}
			required={isRequired}
			onChange={onChangeWithAnalytics}
			onInputChange={onInputChangeWithAnalytics}
			isValidNewOption={isValidNewOption}
			label={ariaLabel || formatMessage(messages.ariaLabelForSelect)}
		/>
	);
};

export default CreatableSelectWithAnalyticsView;
